import Images from '../Utils/images';

interface Props {
  infoText?: string;
  theme?: 'dark' | 'light';
  icon?: React.ReactElement | string;
}

/**
 * Renders an info component with an icon and text, adaptable to light or dark themes.
 * @example
 * Info({ infoText: 'This is information', theme: 'light', icon: customIcon })
 * <div class="reel_theme_info info_light">...</div>
 * @param {Object} props - The properties for the Info component.
 * @param {string} props.infoText - The text to be displayed in the info box.
 * @param {string} [props.theme='dark'] - The theme of the info box, either 'dark' or 'light'.
 * @param {string} [props.icon=Images.INFO_ICON] - The icon to be used in dark theme mode.
 * @returns {JSX.Element} A JSX element representing the info box.
 * @description
 *   - The function defaults to a 'dark' theme if no theme is provided.
 *   - If a 'light' theme is specified, a default light-theme icon is used.
 *   - The component adjusts class names based on the selected theme to apply appropriate styles.
 */
export default function Info(props: Props) {
  const { infoText, theme = 'dark', icon = Images.INFO_ICON } = props;
  return (
    <div
      className={`reel_theme_info  ${
        theme === 'dark' ? 'info_dark' : 'info_light'
      }`}
    >
      <div className="infoIcon">
        <img
          src={theme === 'dark' ? icon : Images.INFO_ICON_LIGHT}
          alt="Info"
        />
      </div>
      <div className={`infoText`}>
        <p>{infoText}</p>
      </div>
    </div>
  );
}
